import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Paper, 
  Table, 
  TableBody, 
  TableRow, 
  TableCell, 
  TableContainer, 
  IconButton,
  Chip,
  SelectChangeEvent,
  Fade,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import Breadcrumb from 'components/Breadcrumb';
import { User, UsersParams, usersService } from './services/UsersService';
import { logger } from 'utils/logger';
import apiService from 'services/ApiService';
import BusinessIcon from '@mui/icons-material/Business';
import { environment } from 'environments/environment';

// Import components from separated files
import UsersTableHeader, { SortKey } from './components/UsersTableHeader';
import UserRow from './components/UserRow';
import UserRowSkeleton from './components/UserRowSkeleton';
import FilterDrawer from './components/FilterDrawer';
import TablePagination from './components/TablePagination';

// Import hooks
import useUsersFilter from './hooks/useUsersFilter';

// Import utilities
import { sortKeyToApiParam, formatDate } from './utils/userUtils';

// Create a component-specific logger
const log = logger.getLogger('UsersPage');

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { accountId } = useParams<{ accountId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialRowsPerPage = parseInt(searchParams.get('size') || '25', 10);
  const initialSortBy = (searchParams.get('sortBy') || 'id') as SortKey;
  const initialSortDirection = (searchParams.get('sortDir') || 'asc') as 'asc' | 'desc';

  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortKey>(initialSortBy);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);

  // Pagination state
  const [page, setPage] = useState<number>(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState<number>(initialRowsPerPage);
  const [paginatedUsers, setPaginatedUsers] = useState<User[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [serverPagination] = useState<boolean>(true);

  // Server-side sorting state
  const [serverSortBy, setServerSortBy] = useState<string>(sortKeyToApiParam[initialSortBy] || 'id');
  const [serverSortDirection, setServerSortDirection] = useState<'asc' | 'desc'>(initialSortDirection);

  // Update URL params when pagination/sorting changes
  const updateUrlParams = useCallback((newPage: number, newSize: number, newSortBy: SortKey, newSortDir: 'asc' | 'desc') => {
    const params = new URLSearchParams(searchParams);

    // Update pagination params
    if (newPage > 0) {
      params.set('page', newPage.toString());
    } else {
      params.delete('page');
    }

    if (newSize !== 25) {
      params.set('size', newSize.toString());
    } else {
      params.delete('size');
    }

    // Update sort params
    if (newSortBy !== 'id') {
      params.set('sortBy', newSortBy);
    } else {
      params.delete('sortBy');
    }

    if (newSortDir !== 'asc') {
      params.set('sortDir', newSortDir);
    } else {
      params.delete('sortDir');
    }

    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Use the filter hook
  const { 
    filterState, 
    tempState, 
    filterDrawerOpen, 
    expandedAccordions, 
    setFilterDrawerOpen, 
    setExpandedAccordions,
    handleEnabledChange,
    handleUsernameFilterChange, 
    applyFilters, 
    clearFilters, 
    removeFilter 
  } = useUsersFilter();

  // Define fetchUsers as a memoized callback
  const fetchUsers = useCallback(async (pageNum = 0, pageSize = rowsPerPage) => {
    if (!accountId) return;
    
    try {
      setLoading(true);
      log.log(`Fetching users data from API (page ${pageNum}, size ${pageSize}, sort ${serverSortBy}, direction ${serverSortDirection})...`);
      
      // Convert the enabled filter state to boolean
      let enabledValue: boolean | undefined;
      if (filterState.enabled === 'enabled') enabledValue = true;
      if (filterState.enabled === 'disabled') enabledValue = false;
      
      // Prepare parameters for the UsersService
      const params: UsersParams = {
        page: pageNum,
        size: pageSize,
        sort: serverSortBy,
        direction: serverSortDirection,
        filters: {
          enabled: enabledValue,
          accountId: Number(accountId),
          robotic: false,
          username: filterState.username
        }
      };
      
      // Call the UsersService
      const result = await usersService.getUsers(params);
      
      log.log(`Found ${result.data.length} users on page ${pageNum+1} (total: ${result.count})`);
      
      setFilteredUsers(result.data);
      setTotalItems(result?.count || 0);
      setError(null);
      
    } catch (err) {
      log.error('Error fetching users:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [accountId, rowsPerPage, serverSortBy, serverSortDirection, filterState.enabled, filterState.username]);

  // Fetch account name
  const fetchAccountName = useCallback(async () => {
    if (!accountId) return;
    
    try {
      const response = await apiService.makeRequest(
        'get', 
        `${environment.services.account.baseUrl}${environment.services.account.basePath}/accounts/${accountId}`
      );
      setAccountName(response?.name || `Account #${accountId}`);
    } catch (err) {
      log.error('Error fetching account details:', err);
      setAccountName(`Account #${accountId}`);
    }
  }, [accountId]);

  // Fetch users on component mount and when relevant state changes
  useEffect(() => {
    if (accountId) {
      // Invalidate cache to ensure we get fresh data
      apiService.invalidateCacheByPattern('/users');
      fetchUsers(page, rowsPerPage);
      fetchAccountName();
    }
  }, [fetchUsers, fetchAccountName, accountId, page, rowsPerPage]);

  // Handle sorting
  const handleSort = useCallback((key: SortKey) => {
    const isAsc = sortBy === key && sortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';

    setSortDirection(newDirection);
    setSortBy(key);

    if (serverPagination) {
      // Update server-side sort parameters and trigger a new API call
      const apiSortParam = sortKeyToApiParam[key];
      setServerSortBy(apiSortParam);
      setServerSortDirection(newDirection);
      // Reset to first page when changing sort
      setPage(0);
      // Update URL params
      updateUrlParams(0, rowsPerPage, key, newDirection);
      // Fetch will be triggered by useEffect when sort state changes
    }
  }, [sortBy, sortDirection, serverPagination, rowsPerPage, updateUrlParams]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage >= 0 && (serverPagination || newPage * rowsPerPage < totalItems)) {
      setPage(newPage);
      updateUrlParams(newPage, rowsPerPage, sortBy, sortDirection);
      // The fetchUsers will be triggered by the useEffect
    }
  }, [serverPagination, rowsPerPage, totalItems, sortBy, sortDirection, updateUrlParams]);

  // Update paginated users for client-side pagination
  useEffect(() => {
    if (!serverPagination) {
      // Only do client-side pagination if serverPagination is false
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      setPaginatedUsers(filteredUsers.slice(startIndex, endIndex));
    } else {
      // For server pagination, filteredUsers already contains just this page
      setPaginatedUsers(filteredUsers);
    }
  }, [filteredUsers, page, rowsPerPage, serverPagination]);

  // Handle rows per page change
  const handleChangeRowsPerPage = useCallback((event: SelectChangeEvent<number>) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing items per page
    updateUrlParams(0, newRowsPerPage, sortBy, sortDirection);
    // Fetch will be triggered by useEffect
  }, [sortBy, sortDirection, updateUrlParams]);

  // Pagination info text
  const getPaginationInfo = useCallback(() => {
    if (filteredUsers.length === 0) {
      return '0 items';
    }
    
    const startItem = serverPagination 
      ? page * rowsPerPage + 1 
      : Math.min(page * rowsPerPage + 1, totalItems);
    
    const endItem = serverPagination 
      ? Math.min(page * rowsPerPage + filteredUsers.length, totalItems)
      : Math.min((page + 1) * rowsPerPage, totalItems);
    
    return `${startItem} - ${endItem} of ${totalItems} items`;
  }, [filteredUsers.length, serverPagination, page, rowsPerPage, totalItems]);

  // Handle edit button click
  const handleEdit = useCallback((userId: number) => {
    navigate(`/accounts/${accountId}/users/${userId}/edit`);
  }, [navigate, accountId]);

  // Handle create user button click
  const handleCreateUser = useCallback(() => {
    navigate(`/accounts/${accountId}/users/create`);
  }, [navigate, accountId]);

  // Handle add solution button click
  const handleAddSolution = useCallback((userId: number) => {
    navigate(`/accounts/${accountId}/users/${userId}/solutions`);
  }, [navigate, accountId]);

  // Handle add subscription button click
  const handleAddSubscription = useCallback((userId: number) => {
    navigate(`/accounts/${accountId}/users/${userId}/subscriptions/add`);
  }, [navigate, accountId]);

  // Memoize table content to avoid unnecessary re-renders
  const tableContent = useMemo(() => {
    if (loading) {
      // Show multiple skeleton rows to better represent the loading state
      return Array.from({ length: 5 }).map((_, index) => (
        <UserRowSkeleton key={`skeleton-${index}`} />
      ));
    }
    
    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center" sx={{ color: 'error.main' }}>
            {error}
          </TableCell>
        </TableRow>
      );
    }
    
    if (paginatedUsers.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">No users found</TableCell>
        </TableRow>
      );
    }
    
    return paginatedUsers.map((user) => (
      <UserRow 
        key={user.id} 
        user={user} 
        accountId={Number(accountId)}
        handleEdit={handleEdit}
        handleAddSolution={handleAddSolution}
        handleAddSubscription={handleAddSubscription}
        formatDate={formatDate}
      />
    ));
  }, [paginatedUsers, loading, error, accountId, handleEdit, handleAddSolution, handleAddSubscription]);

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      <Breadcrumb 
        items={[
          {
            label: 'Accounts',
            path: '/accounts',
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          { 
            label: accountName,
            path: `/accounts/${accountId}/edit`,
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          },
          {
            label: 'Users',
            icon: <PersonIcon fontSize="small" sx={{ fontSize: '1rem' }} />
          }
        ]}
      />
      
      <Fade in={true} timeout={600}>
        <Paper 
          elevation={2}
          sx={{ 
            width: '100%', 
            mb: 3, 
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)'
            }
          }}
        >
          <Box 
            p={2.5} 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              background: theme.palette.background.default
            }}
          >
            <Box display="flex" alignItems="center">
              <Box display="flex" flexWrap="wrap" alignItems="center" mr={1}>
                {filterState.activeFilters.map((filter, index) => (
                  <Chip 
                    key={index} 
                    label={filter} 
                    size="small"
                    onDelete={() => removeFilter(filter)} 
                    sx={{ 
                      mr: 0.7, 
                      mb: 0.5,
                      borderRadius: '16px',
                      fontWeight: 500,
                      transition: 'all 0.2s',
                      '&:hover': {
                        opacity: 0.9,
                        transform: 'translateY(-1px)'
                      }
                    }} 
                  />
                ))}
              </Box>
              <Tooltip title="Filter users">
                <IconButton 
                  size="medium" 
                  onClick={() => setFilterDrawerOpen(true)}
                  sx={{ 
                    ml: 0.5,
                    borderRadius: '12px',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      backgroundColor: alpha(theme.palette.primary.main, 0.1) 
                    }
                  }}
                >
                  <FilterAltIcon />
                </IconButton>
              </Tooltip>
              <Button 
                variant="contained" 
                color="primary" 
                size="medium"
                onClick={handleCreateUser}
                startIcon={<AddIcon />}
                sx={{ 
                  ml: 1.5,
                  px: 2,
                  py: 1,
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 14px rgba(0,0,0,0.14)'
                  }
                }}
              >
                Create User
              </Button>
            </Box>
          </Box>

          <FilterDrawer 
            open={filterDrawerOpen}
            onClose={() => setFilterDrawerOpen(false)}
            tempState={tempState}
            expandedAccordions={expandedAccordions}
            setExpandedAccordions={setExpandedAccordions}
            handleEnabledChange={handleEnabledChange}
            handleUsernameFilterChange={handleUsernameFilterChange}
            applyFilters={applyFilters}
            clearFilters={clearFilters}
          />

          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <UsersTableHeader 
                sortBy={sortBy}
                sortDirection={sortDirection}
                handleSort={handleSort}
              />
              <TableBody
                sx={{
                  '& .MuiTableRow-root:nth-of-type(odd)': {
                    backgroundColor: alpha(theme.palette.background.default, 0.4),
                  }
                }}
              >
                {tableContent}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination 
            page={page}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            paginatedUsers={paginatedUsers}
            serverPagination={serverPagination}
            handlePageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            getPaginationInfo={getPaginationInfo}
          />
        </Paper>
      </Fade>
    </Container>
  );
};

export default UsersPage;